import { ArrowsOutLineHorizontalIcon, PlusIcon, WarningCircleIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  type AspectRatioToken,
  type Placement,
  type Platform,
  getPlacement,
  ratioToNumber,
} from '@/lib/platform-matrix/placement-matrix';
import { ICON_BUTTON, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * Per-variant lifecycle. Supplied by the store layer — this component never
 * infers a state from the absence of a thumbnail, because "no image yet" and
 * "generation failed" look identical from here and mean opposite things.
 */
export type VariantState = 'ready' | 'generating' | 'needs-review' | 'failed' | 'empty';

export interface RatioVariant {
  ratio: AspectRatioToken;
  state: VariantState;
  /** Rendered composite at low res. Absent while empty/generating. */
  thumbnailUrl?: string;
  /** 0–100, only meaningful while `generating`. */
  progress?: number;
}

export interface RatioVariantStripProps {
  variants: readonly RatioVariant[];
  selected: AspectRatioToken;
  onSelect: (ratio: AspectRatioToken) => void;
  /** Platforms this concept ships to — drives the `+` menu from the matrix. */
  platforms?: readonly { platform: Platform; placement?: Placement }[];
  onAdd?: (ratio: AspectRatioToken) => void;
  /** Actions that apply to the sizes as a set (e.g. regenerate), shown under the header. */
  actions?: React.ReactNode;
  className?: string;
}

const THUMB_HEIGHT_PX = 56;

/**
 * The `Size variants` strip — a thumbnail radio group, one chip per aspect
 * ratio, rendered at its REAL shape.
 *
 * Text chips ("1:1", "9:16") would be cheaper and are what Mora ships today in
 * `AspectRatioPicker`, but a marketer choosing a placement is making a shape
 * judgement: a 9:16 that will be a full-screen Reel and a 1.91:1 link card are
 * not two labels, they are two different products. The shape has to be visible.
 *
 * WHY (measured, Hightouch): a 3-chip strip inside an 84.5px block with 10px
 * padding, first chip ringed. We beat their three anonymous thumbnails by
 * driving the `+` menu from `placement-matrix.ts`, which knows WHY each ratio
 * exists and which one each platform recommends.
 */
export function RatioVariantStrip({
  variants,
  selected,
  onSelect,
  platforms,
  onAdd,
  actions,
  className,
}: RatioVariantStripProps): JSX.Element {
  const present = new Set(variants.map((v) => v.ratio));
  const addable = platforms ? remainingRatios(platforms, present) : [];
  /* See `PropertyRow`'s cascade note — a menu portalled to `document.body`
     inside a workspace Inspector closes the whole workbench on dismiss. */
  const menuContainer = useDialogContentContainer();

  return (
    <section className={cn('px-2.5 py-2.5', className)}>
      <header className="flex items-center justify-between gap-2">
        {/* WHY (measured, Hightouch): the `⇔` glyph runs at 12px immediately
            left of a 12px header, with the `+` right-aligned on the same line.
            Ours ran at 14px against an 11px label — the icon out-weighed the
            words it was labelling. `size-3` is 12px; the label stays on Mora's
            own 11px SECTION_LABEL so it agrees with `Layers`, `Content` and
            every accordion title in the two rails rather than with one
            competitor value. */}
        <span className={cn(SECTION_LABEL, 'flex items-center gap-1.5')}>
          <ArrowsOutLineHorizontalIcon className="size-3" />
          Size variants
        </span>
        {onAdd && addable.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="Add a size variant"
              className={cn(
                ICON_BUTTON,
                'inline-flex size-5 items-center justify-center rounded-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <PlusIcon className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56" container={menuContainer}>
              {addable.map((group, index) => (
                <React.Fragment key={group.platform}>
                  {index > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuLabel className={SECTION_LABEL}>{group.platform}</DropdownMenuLabel>
                  {group.ratios.map((option) => (
                    <DropdownMenuItem
                      key={`${group.platform}-${option.ratio}`}
                      onSelect={() => onAdd(option.ratio)}
                      className="gap-1.5 rounded-sm px-2 py-1.5 text-caption"
                    >
                      <RatioGlyph ratio={option.ratio} />
                      <span className="flex-1">{option.ratio}</span>
                      {option.recommended ? (
                        <span className="text-detail text-muted-foreground">Recommended</span>
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </header>

      {actions ? <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div> : null}

      {/*
        🔴 THE PADDING IS NOT DECORATION — it is what stops the selected
        thumbnail's ring being sliced off, and removing it re-breaks this.

        `overflow-x-auto` does NOT leave the vertical axis alone. Per CSS
        overflow, when one axis is set to anything other than `visible`, the
        other computes to `auto` — so this scroll container clips TOP and
        BOTTOM as well as the sides. Measured live before the fix: the
        container's content box started at y=86 and the selected thumb's own
        box also started at y=86, with `paddingTop: 0px`. A `ring-2` is drawn
        2px OUTSIDE the element's box, so those 2px landed at y=84 — outside
        the container — and were cut. Founder, 2026-08-21: "those two things
        are literally the top borders of those little cards… you can't even
        see. It's cut off."

        The horizontal axis already solved this and the vertical one was just
        never given the same treatment: `-mx-0.5 px-0.5` is exactly 2px of
        clip-safe gutter either side, cancelled by a negative margin so the
        rhythm does not shift. This does the same vertically — `pt-1` for the
        room, `mt-1` instead of `mt-2` so the gap under the header stays 8px.
        Padding is inside the scrollable area, so a ring drawn into it is
        safe; margin is not.
      */}
      <div
        role="radiogroup"
        aria-label="Size variants"
        className="-mx-0.5 mt-1 flex items-end gap-2 overflow-x-auto px-0.5 pb-1 pt-1"
      >
        {variants.map((variant) => (
          <VariantThumb
            key={variant.ratio}
            variant={variant}
            selected={variant.ratio === selected}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
}

function VariantThumb({
  variant,
  selected,
  onSelect,
}: {
  variant: RatioVariant;
  selected: boolean;
  onSelect: (ratio: AspectRatioToken) => void;
}): JSX.Element {
  const width = Math.round(THUMB_HEIGHT_PX * ratioToNumber(variant.ratio));

  const thumb = (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${variant.ratio}${variant.state === 'needs-review' ? ', needs review' : ''}`}
      onClick={() => onSelect(variant.ratio)}
      className="group flex shrink-0 flex-col items-center gap-1 focus-visible:outline-none"
    >
      <span
        style={{ width, height: THUMB_HEIGHT_PX }}
        className={cn(
          'relative block overflow-hidden rounded-sm bg-muted transition-shadow dark:bg-muted/40',
          // ONE accent for selection across the whole workbench: the same
          // `--selection-accent` that rings a selected layer row and lights an
          // active toggle. `ring-primary` was the first instinct and it is
          // wrong here — Mora's `--primary` is warm ink in light and near-white
          // in dark, so the strip would have spoken a different selection
          // language from the panel three inches to its left.
          selected
            ? 'ring-2 ring-selection-accent'
            : 'ring-1 ring-border/40 group-hover:ring-border',
          'group-focus-visible:ring-2 group-focus-visible:ring-ring',
        )}
      >
        {variant.thumbnailUrl ? (
          <img
            src={variant.thumbnailUrl}
            alt=""
            className="size-full object-cover"
            draggable={false}
          />
        ) : null}
        {variant.state === 'generating' ? (
          // /20, not /60: a variant that has no artwork yet must not be the
          // BRIGHTEST chip in the strip. The first pass made the one empty
          // 16:9 slab out-shout three finished creatives beside it.
          <span className="absolute inset-0 flex animate-pulse items-center justify-center bg-muted/20">
            {typeof variant.progress === 'number' ? (
              <span className="text-micro tabular-nums text-muted-foreground">
                {Math.round(variant.progress)}%
              </span>
            ) : null}
          </span>
        ) : null}
        {variant.state === 'failed' ? (
          <span className="absolute inset-0 flex items-center justify-center bg-destructive/10">
            <WarningCircleIcon className="size-4 text-destructive" />
          </span>
        ) : null}
      </span>
      <span className="flex items-center gap-1">
        <span
          className={cn(
            'text-micro tabular-nums',
            selected ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {variant.ratio}
        </span>
        {variant.state === 'needs-review' ? (
          <span aria-hidden className="size-1.5 rounded-full bg-warning" />
        ) : null}
      </span>
    </button>
  );

  if (variant.state !== 'needs-review') return thumb;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{thumb}</TooltipTrigger>
      <TooltipContent side="bottom">
        This size has manual changes. Edits to other sizes won&apos;t overwrite them.
      </TooltipContent>
    </Tooltip>
  );
}

/** A tiny outline of the ratio, so the `+` menu is also shape-first. */
function RatioGlyph({ ratio }: { ratio: AspectRatioToken }): JSX.Element {
  const n = ratioToNumber(ratio);
  const height = 14;
  const width = Math.max(4, Math.round(height * n));
  return (
    <span
      aria-hidden
      style={{ width, height }}
      className="inline-block shrink-0 border border-muted-foreground/50"
    />
  );
}

/**
 * The ratios this concept's platforms support that are not already on the
 * strip, grouped by platform with the matrix's own `recommendedRatio` flagged.
 * The matrix invariant is `recommendedRatio === ratioOptions[0]`.
 */
function remainingRatios(
  platforms: readonly { platform: Platform; placement?: Placement }[],
  present: ReadonlySet<AspectRatioToken>,
): Array<{ platform: Platform; ratios: Array<{ ratio: AspectRatioToken; recommended: boolean }> }> {
  const groups: Array<{
    platform: Platform;
    ratios: Array<{ ratio: AspectRatioToken; recommended: boolean }>;
  }> = [];

  for (const { platform, placement } of platforms) {
    const row = getPlacement(platform, placement);
    if (!row) continue;
    const ratios = row.ratioOptions
      .filter((ratio) => !present.has(ratio))
      .map((ratio) => ({ ratio, recommended: ratio === row.recommendedRatio }));
    if (ratios.length > 0) groups.push({ platform, ratios });
  }

  return groups;
}
